import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate,
} from 'react-router-dom';

import { usersMutations } from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, UserForm, UserProjectsPicker,
} from '@/components';
import { AppRoute } from '@/constants';
import { useBackToOrList } from '@/hooks';
import { UserCreatePayload } from '@/types/payload.types';

export const UserCreatePage = () => {
    const navigate = useNavigate();
    const handleBack = useBackToOrList(AppRoute.Users);
    const { token } = theme.useToken();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<UserCreatePayload | null>(null);
    const [projectsIds, setProjectsIds] = useState<string[]>([]);

    const createMutationOptions = usersMutations.create();
    const { mutate: createUser, isPending } = useMutation(createMutationOptions);

    const handleSubmit = useCallback((data: {
        email?: string | null;
        nickname: string;
        role: string;
        name?: string | null;
        is_active?: boolean;
        comment?: string | null;
        password?: string | null;
        password_confirmation?: string | null;
        new_password?: boolean | null;
    }) => {
        if (!data.password || !data.password_confirmation) {
            return;
        }
        const createPayload: UserCreatePayload = {
            nickname: data.nickname,
            name: data.name,
            is_active: true,
            comment: data.comment,
            role: data.role,
            password: data.password,
            password_confirmation: data.password_confirmation,
            projects_ids: projectsIds,
        };
        setFormData(createPayload);
        setConfirmModalOpen(true);
    }, [projectsIds]);

    const handleConfirm = useCallback(() => {
        if (formData) {
            createUser(formData, {
                onSuccess: () => {
                    setConfirmModalOpen(false);
                    setFormData(null);
                    navigate(AppRoute.Users);
                },
            });
        }
    }, [formData, createUser, navigate]);

    const handleCancel = useCallback(() => {
        handleBack();
    }, [handleBack]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    const confirmEntityLabel = formData?.nickname?.trim()
        || formData?.name?.trim()
        || undefined;

    return (
        <>
            <Helmet>
                <title>Створити користувача</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={16} xl={12}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={AppRoute.Users}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Створити користувача
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <UserForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isPending}
                            cancelButtonText="Відмінити"
                            cancelTo={AppRoute.Users}
                        />
                        <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
                            Проекти
                        </Typography.Title>
                        <UserProjectsPicker
                            value={projectsIds}
                            onChange={setProjectsIds}
                            disabled={isPending}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="create"
                entityType="user"
                entityName={confirmEntityLabel}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
